import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable, Subscription, timer } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  ExecutarOpts,
  FlowContextoAcao,
  FlowExecutionStep,
  FlowJobStatus,
  FlowJobStatusType,
  FlowMonitorState,
} from '../models/conciliadora.models';
import { ConciliadoraService } from './conciliadora.service';

/**
 * Servico que orquestra a execucao assincrona de fluxos NEWMODE da Conciliadora
 * e mantem um estado reativo (`state$`) consumido pelo componente
 * `FlowExecutionMonitorComponent` (drawer lateral).
 *
 * Ciclo:
 *  1. `start(...)` chama `iniciarFlowAsync` -> recebe `idjob`.
 *  2. Inicia polling em `consultarJobStatus(idjob)` a cada 1.5s.
 *  3. Estados emitidos: PENDING -> RUNNING -> COMPLETED | ERROR | STOPPED.
 *  4. Ao terminar, `result.flowExecutionList` (quando presente) e parseado
 *     em `steps[]` para a timeline.
 *  5. `stop()` solicita cancel ao backend e marca STOPPED.
 *  6. `reset()` zera o estado para uma nova execucao.
 *
 * Persistencia: o `idjob` e salvo em `localStorage` para sobreviver a refresh.
 * Ao reabrir a tela, `tryResume()` retoma o monitor caso o job ainda exista.
 *
 * NAO e providedIn: 'root' - precisa ser providenciado em
 * `conciliadora-main.module.ts` para isolamento por modulo.
 */
@Injectable()
export class FlowMonitorService {
  private static readonly POLL_INTERVAL_MS = 1500;
  private static readonly STORAGE_KEY = 'mktp.conciliadora.flowMonitor.activeJob';

  private readonly destroyRef = inject(DestroyRef);

  private readonly _state$ = new BehaviorSubject<FlowMonitorState>(this.initialState());
  readonly state$: Observable<FlowMonitorState> = this._state$.asObservable();

  private pollSubscription: Subscription | null = null;
  private elapsedTickSubscription: Subscription | null = null;

  constructor(private svc: ConciliadoraService) {}

  /** Snapshot atual (sincrono). */
  get snapshot(): FlowMonitorState {
    return this._state$.getValue();
  }

  /** Inicia uma nova execucao. */
  start(
    idconf: number,
    rows: Record<string, any>[],
    opts: ExecutarOpts,
    title: string,
    contextoAcao: FlowContextoAcao
  ): void {
    this.cancelInternalPolling();

    const initial: FlowMonitorState = {
      ...this.initialState(),
      starting: true,
      title,
      contextoAcao,
      idconf,
      rowsCount: rows?.length ?? 0,
      status: 'PENDING',
      startedAt: Date.now(),
    };
    this._state$.next(initial);
    this.startElapsedTick();

    this.svc.iniciarFlowAsync(idconf, rows, opts).subscribe({
      next: (res) => {
        if (!res || !res.ok || !res.idjob) {
          this.applyError(res?.message ?? 'Backend retornou resposta invalida ao iniciar fluxo.');
          return;
        }
        this.persistIdjob(res.idjob);
        this._state$.next({
          ...this._state$.getValue(),
          starting: false,
          idjob: res.idjob,
          threadName: res.threadName ?? null,
          status: (res.status as FlowJobStatusType) ?? 'PENDING',
        });
        this.startPolling(res.idjob);
      },
      error: (err) => {
        this.applyError(err?.message ?? err?.error?.message ?? 'Falha ao iniciar fluxo.');
      },
    });
  }

  /** Solicita cancelamento e atualiza estado para STOPPED. */
  stop(): Observable<boolean> {
    const s = this._state$.getValue();
    if (!s.idjob) return of(false);
    return new Observable<boolean>((subscriber) => {
      this.svc.cancelarJob(s.idjob!).subscribe({
        next: (res) => {
          this.cancelInternalPolling();
          this.clearPersisted();
          this._state$.next({
            ...this._state$.getValue(),
            status: 'STOPPED',
            finishedAt: Date.now(),
            elapsedMs: Date.now() - (this._state$.getValue().startedAt ?? Date.now()),
          });
          subscriber.next(!!res?.ok);
          subscriber.complete();
        },
        error: () => {
          subscriber.next(false);
          subscriber.complete();
        },
      });
    });
  }

  /** Limpa estado para iniciar nova execucao. */
  reset(): void {
    this.cancelInternalPolling();
    this.clearPersisted();
    this._state$.next(this.initialState());
  }

  /**
   * Tenta retomar polling de um job persistido em localStorage (caso o usuario
   * tenha recarregado a pagina). Retorna true se houve retomada.
   */
  tryResume(): boolean {
    const idjob = this.loadPersistedIdjob();
    if (!idjob) return false;
    this._state$.next({
      ...this.initialState(),
      idjob,
      status: 'RUNNING',
      startedAt: Date.now(),
      title: 'Execucao em andamento (retomada)',
    });
    this.startElapsedTick();
    this.startPolling(idjob);
    return true;
  }

  // ----------------------------------------------------------------------
  // Internos
  // ----------------------------------------------------------------------

  private startPolling(idjob: string): void {
    this.cancelInternalPolling();
    this.pollSubscription = timer(0, FlowMonitorService.POLL_INTERVAL_MS)
      .pipe(
        switchMap(() =>
          this.svc.consultarJobStatus(idjob).pipe(
            catchError((err) => {
              return of<FlowJobStatus>({
                idjob,
                status: 'ERROR',
                error: true,
                errorMessage: err?.message ?? 'Falha de comunicacao com o servidor.',
                startedAt: this._state$.getValue().startedAt ?? Date.now(),
                finishedAt: Date.now(),
                elapsedMs: 0,
                result: null,
              });
            })
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((status) => this.applyStatus(status));
  }

  private applyStatus(status: FlowJobStatus): void {
    const current = this._state$.getValue();

    const next: FlowMonitorState = {
      ...current,
      status: status.status,
      error: !!status.error,
      errorMessage: status.errorMessage ?? null,
      elapsedMs: status.elapsedMs ?? current.elapsedMs,
      startedAt: status.startedAt ?? current.startedAt,
      finishedAt: status.finishedAt > 0 ? status.finishedAt : current.finishedAt,
      threadName: status.threadName ?? current.threadName,
      rawResult: status.result ?? current.rawResult,
    };

    if (status.status === 'NOT_FOUND') {
      next.status = 'ERROR';
      next.error = true;
      next.errorMessage = 'Job nao encontrado no servidor (pode ter expirado).';
      next.finishedAt = Date.now();
    }

    if (this.isTerminal(next.status)) {
      next.steps = this.parseStepsFromResult(status.result);
      this.cancelInternalPolling();
      this.clearPersisted();

      // Detecta "completed silencioso": status do backend = COMPLETED, mas algum
      // step interno marcou success=false. O FlowEngine.processError sai com
      // flowEnded=true quando onError.behaivor=null, deixando o motor entregar
      // o resultado sem exception. Para o usuario isso e erro, nao sucesso.
      if (next.status === 'COMPLETED') {
        const stepsWithError = (next.steps ?? []).filter((s) => s.error);
        if (stepsWithError.length > 0) {
          next.status = 'ERROR';
          next.error = true;
          const firstFailed = stepsWithError[0];
          const stepLabel = firstFailed.name
            ? `"${firstFailed.name}"`
            : firstFailed.key
              ? `step "${firstFailed.key}"`
              : 'step';
          next.errorMessage = `Fluxo terminou sem propagar excecao, mas ${stepLabel} falhou: ${firstFailed.errorMessage ?? 'success=false'}.`;
        }
      }
    }

    this._state$.next(next);
  }

  private applyError(message: string): void {
    this.cancelInternalPolling();
    this.clearPersisted();
    this._state$.next({
      ...this._state$.getValue(),
      starting: false,
      status: 'ERROR',
      error: true,
      errorMessage: message,
      finishedAt: Date.now(),
    });
  }

  private startElapsedTick(): void {
    this.cancelElapsedTick();
    this.elapsedTickSubscription = timer(0, 1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const s = this._state$.getValue();
        if (!s.startedAt || this.isTerminal(s.status)) {
          return;
        }
        this._state$.next({
          ...s,
          elapsedMs: Date.now() - s.startedAt,
        });
      });
  }

  private cancelInternalPolling(): void {
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = null;
    this.cancelElapsedTick();
  }

  private cancelElapsedTick(): void {
    this.elapsedTickSubscription?.unsubscribe();
    this.elapsedTickSubscription = null;
  }

  private isTerminal(status: FlowJobStatusType | 'IDLE' | 'PARTIAL'): boolean {
    return (
      status === 'COMPLETED' ||
      status === 'ERROR' ||
      status === 'STOPPED' ||
      status === 'PARTIAL'
    );
  }

  private initialState(): FlowMonitorState {
    return {
      idjob: null,
      threadName: null,
      idconf: null,
      status: 'IDLE',
      error: false,
      errorMessage: null,
      steps: [],
      rawResult: null,
      startedAt: null,
      finishedAt: null,
      elapsedMs: 0,
      rowsCount: 0,
      title: '',
      contextoAcao: null,
      starting: false,
    };
  }

  /**
   * Converte o `flowExecutionList` retornado pelo backend (estrutura
   * `{ stepKey: [FlowExecution, ...] }`) num array linear ordenado por
   * dhStart para alimentar a timeline da UI.
   *
   * Tolera variacoes de formato (array direto, JSON string, ou null).
   */
  private parseStepsFromResult(result: any): FlowExecutionStep[] {
    if (!result) return [];
    let raw: any = result;
    if (typeof raw === 'string') {
      try { raw = JSON.parse(raw); } catch { return []; }
    }
    if (!raw || typeof raw !== 'object') return [];

    const steps: FlowExecutionStep[] = [];

    if (Array.isArray(raw)) {
      for (const item of raw) this.collectStep(item, steps);
    } else {
      for (const key of Object.keys(raw)) {
        const value = raw[key];
        if (Array.isArray(value)) {
          for (const item of value) this.collectStep(item, steps, key);
        } else {
          this.collectStep(value, steps, key);
        }
      }
    }

    steps.sort((a, b) => {
      const da = a.dhStart ?? '';
      const db = b.dhStart ?? '';
      return da.localeCompare(db);
    });
    for (const s of steps) {
      if (s.dhStart && s.dhEnd && s.durationMs == null) {
        const start = Date.parse(s.dhStart);
        const end = Date.parse(s.dhEnd);
        if (!isNaN(start) && !isNaN(end)) {
          s.durationMs = end - start;
        }
      }
    }
    return steps;
  }

  private collectStep(item: any, out: FlowExecutionStep[], fallbackKey?: string): void {
    if (!item || typeof item !== 'object') return;

    // Campos vem do br.com.temapi.runtime.log.FlowExecution serializado por Gson:
    //   - inputParameter / outputParameter (LinkedHashMap → object) - nomes SINGULARES
    //   - result (ArrayList<FlowLogExecution>) - lista de execucoes internas
    //   - outPut (ArrayList legacy) - fallback antigo
    //   - guid (=step.key), name, type, dhStart, dhEnd
    //   - executionId, parentExecutionId, executionPath, parentId
    const innerExecutions: any[] = Array.isArray(item.result)
      ? item.result
      : Array.isArray(item.outPut)
        ? item.outPut
        : [];

    const hasUnsuccessful = innerExecutions.some(
      (r) => r && typeof r === 'object' && r.success === false
    );
    const errorFlag = !!item.error || hasUnsuccessful;

    let errorMsg: string | undefined = item.errorMessage;
    if (!errorMsg && hasUnsuccessful) {
      const failed = innerExecutions.find(
        (r) => r && typeof r === 'object' && r.success === false
      );
      if (failed) {
        const sc = failed.statusCode ?? failed.responseStatusCode;
        const rawBody = failed.responseBody ?? failed.body;
        const body = typeof rawBody === 'string'
          ? rawBody.substring(0, 300)
          : rawBody != null ? JSON.stringify(rawBody).substring(0, 300) : '';
        errorMsg = sc != null
          ? `Status ${sc}${body ? ' - ' + body : ''}`
          : (body || 'Step retornou success=false');
      }
    }

    // Para o painel "Detalhes" do componente: preferimos mostrar o input/output
    // resolvido (mapas) e tambem expor o `result` (lista de execucoes - util pra
    // REQUEST onde tras url/body/responseBody/statusCode de cada chamada).
    const outputCombined = this.buildOutputSummary(item, innerExecutions);

    const step: FlowExecutionStep = {
      key: String(item.guid ?? item.key ?? item.stepKey ?? fallbackKey ?? ''),
      name: item.name ?? item.stepName ?? item.label,
      type: item.type ?? item.stepType,
      dhStart: item.dhStart,
      dhEnd: item.dhEnd,
      durationMs: typeof item.durationMs === 'number' ? item.durationMs : undefined,
      parentExecutionId: item.parentExecutionId,
      executionPath: item.executionPath,
      input: item.inputParameter ?? item.input ?? item.inputParameters,
      output: outputCombined,
      error: errorFlag,
      errorMessage: errorMsg,
    };
    out.push(step);
  }

  /**
   * Monta um output amigavel para o painel de detalhes. Inclui o mapa final
   * (outputParameter) + um resumo das execucoes internas (`result`), que para
   * REQUEST tras url/statusCode/responseBody (essencial pra diagnosticar
   * "completed silencioso" como o caso do Login Sankhya com status 500).
   */
  private buildOutputSummary(item: any, innerExecutions: any[]): any {
    const summary: any = {};
    if (item.outputParameter && typeof item.outputParameter === 'object') {
      summary.outputParameter = item.outputParameter;
    }
    if (innerExecutions.length > 0) {
      summary.executions = innerExecutions.map((r) => {
        if (!r || typeof r !== 'object') return r;
        // Preserva tudo, mas garante que campos de diagnostico fiquem no topo
        const { success, statusCode, url, methodName, responseBody, body, ...rest } = r;
        return {
          success: success ?? undefined,
          statusCode: statusCode ?? undefined,
          methodName: methodName ?? undefined,
          url: url ?? undefined,
          responseBody: responseBody ?? undefined,
          body: body ?? undefined,
          ...rest,
        };
      });
    }
    // Fallback: se nao encontrou nenhum dos dois, devolve o item bruto pro user
    // pelo menos ver alguma coisa em vez de "(vazio)".
    if (Object.keys(summary).length === 0) {
      return item;
    }
    return summary;
  }

  // ------- localStorage helpers -------

  private persistIdjob(idjob: string): void {
    try {
      localStorage.setItem(FlowMonitorService.STORAGE_KEY, idjob);
    } catch { /* quota exceeded - ignorar */ }
  }

  private loadPersistedIdjob(): string | null {
    try {
      return localStorage.getItem(FlowMonitorService.STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private clearPersisted(): void {
    try {
      localStorage.removeItem(FlowMonitorService.STORAGE_KEY);
    } catch { /* ignore */ }
  }
}
